import Phaser from 'phaser';
import { installHalloweenBossPortal } from '@/game/ui/HalloweenBossPortal';
import halloween from '../../../server/api/src/game-config/halloween.config.json';
import { BIOMES_BY_ID } from '@/config/biomes.config';
import { DESTRUCTIBLES_BY_ID } from '@/config/destructibles.config';
import { PETS_BY_ID } from '@/config/pets.config';
import { coinSpriteCount, coinGainMult, magnetMult } from '@/config/balance.config';
import { activeEffectMult } from '@/config/potions.config';
import { saveManager } from '@/save';
import { useGameStore } from '@/ui/store/gameStore';
import { eventBus } from '@/shared/EventBus';
import { InputSystem } from '@/game/systems/InputSystem';
import { TargetingSystem } from '@/game/systems/TargetingSystem';
import { Player } from '@/game/entities/Player';
import { Destructible } from '@/game/entities/Destructible';
import { Pet, type PetPositionConstraint } from '@/game/entities/Pet';
import { Coin } from '@/game/entities/Coin';
import { spawnDestructionFX } from '@/game/fx/CoinBurst';
import { spawnGemPickup } from '@/game/fx/GemPickup';
import { shakeForDestructible } from '@/game/fx/ScreenShake';
import { showEduBubble, type EduBubble } from '@/game/fx/EduSpeech';
import { showNpcSpeech, type NpcSpeechBubble } from '@/game/fx/NpcSpeech';
import { Haptics } from '@/game/fx/Haptics';
import { DecorationAnimator } from '@/game/fx/DecorationAnimator';
import { BookPageFlipSystem } from '@/game/fx/BookPageFlipSystem';
import { startTeleport, biomeAccent, PLAZA_ACCENT } from '@/game/fx/teleport';
import {
  assetBundleManager,
  getWorldAssetBundle,
  resolvePlayableBiomeId,
} from '@/game/assets/AssetBundleManager';
import { AmbientFxSystem } from '@/game/fx/AmbientFxSystem';
import { AMBIENT_FX_PRESETS } from '@/config/ambientFx.config';
import { AtmosphereSystem } from '@/game/fx/AtmosphereSystem';
import { ATMOSPHERE_PRESETS } from '@/config/atmosphere.config';
import { ParallaxBackgroundSystem } from '@/game/fx/ParallaxBackgroundSystem';
import { PARALLAX_PRESETS } from '@/config/parallax.config';
import { AmbientCrittersSystem } from '@/game/fx/AmbientCrittersSystem';
import { CRITTERS_PRESETS } from '@/config/critters.config';
import type { PetInstance } from '@/domain/types/pet';
import type { BiomeMovementRules } from '@/domain/types/biome';
import { getEventByBiomeId, getEventById, isEventActive } from '@/config/events.config';
import { economyApi, ApiError } from '@/net/api';
import { runEconomyAction, ECONOMY_RESYNC_CODE } from '@/net/economyActions';
import {
  canUseWorldInteraction,
  WorldInteractionMarker,
} from '@/game/ui/WorldInteractionMarker';
import { WorldInteractionController } from '@/game/ui/WorldInteractionController';
import { addCoins, type Coins } from '@/domain/economy/coins';

interface EventDailyNpc {
  eventId: string;
  x: number;
  y: number;
  sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image;
  hitZone: Phaser.GameObjects.Zone;
  arrow?: Phaser.GameObjects.Image;
  marker: WorldInteractionMarker;
  speech?: NpcSpeechBubble;
  nextAvailabilityCheckAt: number;
}

interface EventPierFishingSpot {
  eventId: string;
  x: number;
  y: number;
  sprite: Phaser.GameObjects.Image;
  hitZone: Phaser.GameObjects.Zone;
  marker: WorldInteractionMarker;
  arrow?: Phaser.GameObjects.Image;
  isClaiming: boolean;
  serverCooldownUntil?: number;
  nextAvailabilityCheckAt: number;
}

interface WalkProjection {
  x: number;
  y: number;
  corrected: boolean;
}

/**
 * Główna scena gameplay'u.
 *
 * Renderuje aktualny biom — tile background, dekoracje (kwiaty, krzaki),
 * destructibles na pozycjach z configu, gracza, pety.
 *
 * Każda tekstura ma fallback na placeholder (`textures.exists()` check), żeby
 * gra działała nawet jeśli któraś PNG-ka jest jeszcze nie wgrana.
 */
export class WorldScene extends Phaser.Scene {
  private inputSystem!: InputSystem;
  private worldInteractions!: WorldInteractionController;
  private player!: Player;
  private destructibles: Destructible[] = [];
  private pets: Pet[] = [];
  private equipAssetSeq = 0;
  private coins: Coin[] = [];
  /** Aktywne dymki edukacyjne (biom Świat Flag) — śledzą pozycję swojego peta. */
  private eduBubbles: Array<{ petDefId: string; bubble: EduBubble }> = [];
  private decorationAnimators: DecorationAnimator[] = [];
  /** Jedna wspólna kolejka ambientowego przewracania stron w otwartych książkach. */
  private bookPageFlipSystem: BookPageFlipSystem | null = null;
  /**
   * Ambient FX system (np. padający śnieg w mountain biomie). `null` gdy
   * aktualny biom nie ma `scene.ambientFx`. Reset block w create() ustawia
   * to na null PRZED nową instancją żeby uniknąć zombie ref z poprzedniego
   * biomu (pattern z sesji 26d).
   */
  private ambientFx: AmbientFxSystem | null = null;
  /**
   * Atmosphere overlay (camera-fixed: winieta + god rays + tint). `null` gdy
   * biom nie ma `scene.atmosphere`. Reset block w create() zeruje to PRZED nową
   * instancją (zombie-state pattern jak ambientFx). Niezależne od ambientFx —
   * biom może mieć oba. Patrz `src/game/fx/AtmosphereSystem.ts`.
   */
  private atmosphere: AtmosphereSystem | null = null;
  /**
   * Parallax tła (camera-fixed warstwa dryfująca wolniej niż świat — głębia).
   * `null` gdy biom nie ma `scene.parallax`. Reset block w create() zeruje to
   * PRZED nową instancją (zombie-state pattern jak ambientFx/atmosphere).
   * Patrz `src/game/fx/ParallaxBackgroundSystem.ts`.
   */
  private parallax: ParallaxBackgroundSystem | null = null;
  /**
   * Ambient critters (dekoracyjne stworzonka tła — motyle/ptaki/biedronki).
   * `null` gdy biom nie ma `scene.critters`. Reset block w create() zeruje to
   * PRZED nową instancją (zombie-state pattern jak pozostałe warstwy wizualne).
   * Patrz `src/game/fx/AmbientCrittersSystem.ts`.
   */
  private critters: AmbientCrittersSystem | null = null;
  /**
   * Pending biome change kolejkowane podczas aktywnej HatchScene (sesja 29A
   * review fix LOW). Bez tego `biome:changed` byłby tylko logowany i ginąłby
   * (UI nie blokuje 100% — external/delayed event mógłby przyjść). Po
   * `hatch:closed` aplikujemy odkładony switch przez `scene.restart()`.
   *
   * `null` = brak pending. String = biomeId do zaaplikowania.
   */
  private pendingBiomeChange: string | null = null;
  private targeting!: TargetingSystem;
  /** Inicjalizowane w `create()` z save'a — domyślnie 0, ustawiamy raz przy starcie sceny. */
  private coinTotal: Coins = '0';
  private power = 0;
  /**
   * Mnożnik złota z equipped petów gold/rainbow (1 + suma `Pet.coinBonus`). Aplikowany
   * w `onDestructibleDestroyed` obok upgrade'u `coin_mult`. Przeliczany przy
   * każdej zmianie składu drużyny (`recalcGoldRainbowCoinBonus`).
   */
  private goldRainbowCoinMult = 1;
  /**
   * Snapshot mnożnika magnesu (`magnetMult(upgrades.magnet_range)`) używany przy
   * spawnowaniu nowych monet. Aktualizowany przy starcie sceny i przy każdym
   * `upgrade:purchased` evencie — coiny już lecące zachowują swój multiplier
   * (przekazany im w konstruktorze), ale wszystkie kolejne dostają nowy.
   *
   * coin_mult i walk_speed czytamy LIVE z save'a w momencie użycia — nie potrzebują
   * snapshotu bo ich miejsce konsumpcji jest jednorazowe (destrukcja, set speed).
   */
  private currentMagnetMult = 1;
  /** Bufor pozycji entity (player + pety) — reuse co frame zamiast alokacji. */
  private readonly entityBuffer: { x: number; y: number }[] = [];
  /** Eventowy NPC dziennej paczki (Capibara Summer). Istnieje tylko w biomie plaży. */
  private eventDailyNpc: EventDailyNpc | null = null;
  /** Eventowy punkt połowu z mola (mały cooldownowy bonus muszelek). */
  private eventPierFishing: EventPierFishingSpot | null = null;
  /** Ograniczenia ruchu aktualnego biomu, np. blokada wejścia do wody poza molem. */
  private movementRules: BiomeMovementRules | null = null;
  private nextWaterSplashAt = 0;
  private nextInflatableCapybaraFxAt = 0;
  private readonly petPositionConstraint: PetPositionConstraint = (x, y) => this.projectWalkablePosition(x, y);

  constructor() {
    super({ key: 'WorldScene' });
  }

  create(): void {
    // ⚠ Reset class fields PRZED czymkolwiek innym (sesja 26d fix bug).
    //
    // Phaser `scene.restart()` (używany przez biome switch w sesji 26) re-używa
    // ten sam instance class'y — wywołuje shutdown + create, ale NIE re-inicjuje
    // pól. Class field initializers (`private foo: Foo[] = []`) odpalają się
    // TYLKO przy `new WorldScene()`. Bez resetu tutaj po restarcie tablice
    // zawierają zombie referencje do destroyowanych GameObjectów z poprzedniego
    // biomu — update() iteruje 2× zawartość, ticki na destroyed sprites trafiają
    // niezdefiniowane pozycje, HP bary skaczą, targeting w nieistniejące cele.
    //
    // `this.pets.length = 0` jest już robione w respawnEquippedPets niżej, ale
    // wolimy mieć JEDNO miejsce reset'u zamiast scattered logic.
    this.destructibles.length = 0;
    this.pets.length = 0;
    this.coins.length = 0;
    this.decorationAnimators.length = 0;
    this.bookPageFlipSystem = null;
    this.entityBuffer.length = 0;
    this.coinTotal = '0';
    this.power = 0;
    this.goldRainbowCoinMult = 1;
    this.currentMagnetMult = 1;
    this.eventDailyNpc = null;
    this.eventPierFishing = null;
    this.movementRules = null;
    this.nextWaterSplashAt = 0;
    this.nextInflatableCapybaraFxAt = 0;
    this.worldInteractions = new WorldInteractionController(
      this,
      () => canUseWorldInteraction(useGameStore.getState()),
    );
    // ambientFx z poprzedniej sceny: Phaser GameObjects już destroyed, ale
    // własna referencja musi być wyczyszczona — inaczej `update()` próbowałby
    // tickować zombie i przesuwać destroyed emittery. Sesja 26d pattern.
    this.ambientFx = null;
    // atmosphere: jak ambientFx — Phaser GameObjects destroyed przy restarcie,
    // ale własna ref musi być wyzerowana żeby update() nie tickował zombie.
    this.atmosphere = null;
    // parallax: ten sam zombie-state pattern co wyżej.
    this.parallax = null;
    // critters: ten sam zombie-state pattern co wyżej.
    this.critters = null;
    this.pendingBiomeChange = null;   // sesja 29A: queue pattern dla biome change

    // Aktualny biom czytany z save'a (sesja 26: dodanie biomu Mountain).
    // Domyślnie 'biome-meadow' (z STARTER_SEED). Dev switch w App.tsx zmienia
    // save.currentBiomeId i emituje 'biome:changed' → restart sceny.
    //
    // Codex sesja 26d review: dawniej `return` przy brakującym biome zostawiał
    // scenę pół-utworzoną — `update()` zakłada istniejącego player'a. Teraz
    // fallbackujemy do `biome-meadow` (default) i logujemy warn. Jeśli to się
    // dzieje na produkcji, save miał zły biomeId (np. po config rename albo
    // corrupt save) — naprawiamy save w locie żeby kolejne sesje były spójne.
    const currentSave = saveManager.getCurrent();
    const requestedBiomeId = currentSave.currentBiomeId;
    const playableBiomeId = resolvePlayableBiomeId(currentSave);
    const biome = BIOMES_BY_ID[playableBiomeId]!;
    if (playableBiomeId !== requestedBiomeId) {
      console.warn(
        `[WorldScene] Biom '${requestedBiomeId}' jest nieznany, wyłączony lub nieodblokowany — fallback do biome-meadow + naprawiam save.`,
      );
      void saveManager.update((current) => ({
        ...current,
        currentBiomeId: 'biome-meadow',
      }));
    }
    assetBundleManager.activateScene(
      this.textures,
      this.anims,
      getWorldAssetBundle(playableBiomeId).id,
      currentSave,
    );
    this.movementRules = biome.movement ?? null;

    // ── World setup ──────────────────────────────────────────────────────────
    this.physics.world.setBounds(0, 0, biome.worldSize.w, biome.worldSize.h);
    this.cameras.main.setBounds(0, 0, biome.worldSize.w, biome.worldSize.h);
    this.cameras.main.setBackgroundColor(biome.scene.backgroundColor);

    // ── Tło: tile sprite z PNG lub fallback grid ─────────────────────────────
    if (this.textures.exists(biome.scene.backgroundTextureKey)) {
      this.add.tileSprite(
        biome.worldSize.w / 2,
        biome.worldSize.h / 2,
        biome.worldSize.w,
        biome.worldSize.h,
        biome.scene.backgroundTextureKey,
      ).setDepth(-100);
    } else {
      this.add.grid(
        biome.worldSize.w / 2, biome.worldSize.h / 2,
        biome.worldSize.w, biome.worldSize.h,
        64, 64,
        0xA8E6A1, 1, 0x7FBF7A, 0.25,
      ).setDepth(-100);
    }

    // ── Duże znaki podłoża (dywany, podesty, alejki) ────────────────────────
    // Proceduralne kształty dają mapie kompozycję bez dokładania ciężkich,
    // pełnoekranowych bitmap. Kolejność w configu jest kolejnością rysowania.
    if (biome.groundMarks?.length) {
      const ground = this.add.graphics().setDepth(-90);
      for (const mark of biome.groundMarks) {
        if (mark.kind === 'polyline') {
          if (mark.points.length < 2) continue;
          const drawPath = (color: number, alpha: number, width: number): void => {
            ground.lineStyle(width, color, alpha);
            ground.beginPath();
            ground.moveTo(mark.points[0].x, mark.points[0].y);
            for (let i = 1; i < mark.points.length; i += 1) {
              ground.lineTo(mark.points[i].x, mark.points[i].y);
            }
            ground.strokePath();
          };
          if (mark.outlineColor != null) {
            drawPath(
              mark.outlineColor,
              mark.outlineAlpha ?? 1,
              mark.width + (mark.outlineWidth ?? 4) * 2,
            );
          }
          drawPath(mark.color, mark.alpha ?? 1, mark.width);
          continue;
        }

        const left = mark.x - mark.width / 2;
        const top = mark.y - mark.height / 2;
        const radius = mark.radius ?? 28;
        const drawFill = (x: number, y: number, color: number, alpha: number): void => {
          ground.fillStyle(color, alpha);
          if (mark.kind === 'ellipse') {
            ground.fillEllipse(x + mark.width / 2, y + mark.height / 2, mark.width, mark.height, 128);
          } else {
            ground.fillRoundedRect(x, y, mark.width, mark.height, radius);
          }
        };

        if (mark.shadow) {
          drawFill(
            left + (mark.shadow.offsetX ?? 10),
            top + (mark.shadow.offsetY ?? 14),
            mark.shadow.color ?? 0x513b26,
            mark.shadow.alpha ?? 0.16,
          );
        }
        drawFill(left, top, mark.fillColor, mark.fillAlpha ?? 1);

        const strokeWidth = mark.strokeWidth ?? 0;
        if (strokeWidth > 0 && mark.strokeColor != null) {
          ground.lineStyle(strokeWidth, mark.strokeColor, mark.strokeAlpha ?? 1);
          if (mark.kind === 'ellipse') {
            // Explicitly close the path so wide loop paths have no wedge at
            // the first/last join (strokeEllipse only closes the shape).
            const ellipse = new Phaser.Geom.Ellipse(mark.x, mark.y, mark.width, mark.height);
            ground.strokePoints(ellipse.getPoints(128), true, true);
          } else {
            ground.strokeRoundedRect(left, top, mark.width, mark.height, radius);
          }
        }
      }
    }

    // ── Dekoracje biomu (kwiaty, krzaki — bez kolizji, czysto wizualne) ──────
    for (const deco of biome.decorations ?? []) {
      if (!this.textures.exists(deco.textureKey)) continue;

      if (deco.tileStrip) {
        const strip = deco.tileStrip;
        const sprite = this.add.tileSprite(
          strip.x ?? 0,
          strip.y,
          strip.width ?? biome.worldSize.w,
          strip.height,
          deco.textureKey,
        );
        sprite
          .setOrigin(0, 0)
          .setTileScale(strip.tileScaleX ?? 1, strip.tileScaleY ?? 1)
          .setDepth(strip.depth ?? -60);
        continue;
      }

      for (const pos of deco.positions ?? []) {
        const sprite = this.add.image(pos.x, pos.y, deco.textureKey);
        const displaySize = deco.displaySize ?? { w: 64, h: 64 };
        sprite.setDisplaySize(displaySize.w, displaySize.h).setDepth(deco.depth ?? -50);   // pod destructibles ale nad tłem
        if (deco.alpha != null) {
          sprite.setAlpha(deco.alpha);
        }
        if (deco.scale != null) {
          sprite.setScale(sprite.scaleX * deco.scale, sprite.scaleY * deco.scale);
        }
        if (pos.rotationDeg != null) {
          sprite.setAngle(pos.rotationDeg);
        }
        if (pos.flipX != null) {
          sprite.flipX = pos.flipX;
        } else if (deco.randomFlip && Math.random() < 0.5) {
          sprite.flipX = true;
        }
        if (deco.motion?.kind === 'float') {
          const offsetX = deco.motion.offsetX ?? 0;
          const offsetY = deco.motion.offsetY ?? 8;
          const rotation = Phaser.Math.DegToRad(deco.motion.rotationDeg ?? 2);
          const floatTween = this.tweens.add({
            targets: sprite,
            x: sprite.x + offsetX,
            y: sprite.y + offsetY,
            rotation,
            duration: deco.motion.durationMs ?? 3200,
            delay: deco.motion.delayMs ?? 0,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
          sprite.setData('floatTween', floatTween);
        }
        if (deco.motion?.kind === 'pageFlip') {
          this.bookPageFlipSystem ??= new BookPageFlipSystem(this);
          this.bookPageFlipSystem.add(sprite, deco.motion);
        }
        if (deco.textureKey === 'deco_beach_capybara_float') {
          this.installInflatableCapybaraInteraction(sprite);
        }
        if (deco.textureKey === 'deco_beach_fish_shadows') {
          this.installEventFishInteraction(sprite);
        }
        // Sway + touch push + sparkle on contact
        if (deco.animated !== false) {
          this.decorationAnimators.push(new DecorationAnimator(this, sprite, deco.textureKey));
        }
      }
    }

    if (biome.id === halloween.event.biomeId && isEventActive(halloween.event.id)) {
      installHalloweenBossPortal(this, this.worldInteractions);
    }
    this.spawnEventDailyTraderNpc(biome.id);
    this.spawnEventPierFishingSpot(biome.id);

    // ── Destructibles ────────────────────────────────────────────────────────
    for (const spawn of biome.destructibleSpawns) {
      const def = DESTRUCTIBLES_BY_ID[spawn.destructibleId];
      if (!def) continue;
      const realKey = def.art.textureKey;
      const textureKey = this.textures.exists(realKey)
        ? realKey
        : this.fallbackPlaceholderFor(spawn.destructibleId);
      for (const pos of spawn.positions ?? []) {
        const d = new Destructible(this, def, pos, textureKey, (self) => this.onDestructibleDestroyed(self));
        this.destructibles.push(d);
      }
    }

    // ── Input + Player ───────────────────────────────────────────────────────
    this.inputSystem = new InputSystem();
    this.inputSystem.attach(this);
    // Player skin z save'a — main.tsx czeka aż gracz wybierze (isFirstRun=false)
    // PRZED createGame(), więc skinId jest gwarantowanie aktualny.
    const skinId = saveManager.getCurrent().profile.avatar.skinId;
    this.player = new Player(this, biome.playerSpawn.x, biome.playerSpawn.y, this.inputSystem, skinId);
    this.cameras.main.startFollow(this.player.sprite, true, 0.12, 0.12);

    // ── Upgrade hooks (sesja 24b) ─────────────────────────────────────────────
    // Applikujemy initial state z save'a, listener niżej aktualizuje live.
    this.applyUpgradesFromSave();

    // ── Equipped pets z save.equipped (zamiast hardcoded starter) ────────────
    this.respawnEquippedPets();
    this.recalcPower();

    // ── Ambient FX (Step 3.B.2) — padający śnieg w mountain, w przyszłości
    //    popiół w wulkanie, liście w lesie. Włączane przez `biome.scene.ambientFx`.
    //    Sesja 28b: `ambientFx` jest ścisłym typem `AmbientFxKey | undefined`,
    //    więc TS gwarantuje że preset istnieje w `AMBIENT_FX_PRESETS`
    //    (`Record<AmbientFxKey, ...>`). Brak runtime warn'u — literówka wpadłaby
    //    na compile.
    const ambientKey = biome.scene.ambientFx;
    if (ambientKey) {
      this.ambientFx = new AmbientFxSystem(this, AMBIENT_FX_PRESETS[ambientKey]);
    }

    // ── Atmosphere overlay — camera-fixed winieta + god rays + tint.
    //    Niezależny od ambientFx (biom może mieć oba). Ścisły typ
    //    `AtmosphereKey | undefined` gwarantuje obecność presetu w
    //    `ATMOSPHERE_PRESETS` (Record), więc literówka = błąd compile-time.
    const atmosphereKey = biome.scene.atmosphere;
    if (atmosphereKey) {
      this.atmosphere = new AtmosphereSystem(this, ATMOSPHERE_PRESETS[atmosphereKey]);
    }

    // ── Parallax tła — warstwa dryfująca wolniej niż świat (głębia).
    //    Fail-soft: brak tekstury warstwy → system pomija ją z warn (gra działa
    //    bez parallaxu dopóki PNG nie zostanie wygenerowany).
    const parallaxKey = biome.scene.parallax;
    if (parallaxKey) {
      this.parallax = new ParallaxBackgroundSystem(this, PARALLAX_PRESETS[parallaxKey]);
    }

    // ── Ambient critters — dekoracyjne stworzonka tła (motyle/ptaki/biedronki).
    //    Fail-soft: typ z brakującą teksturą jest pomijany z warn.
    const crittersKey = biome.scene.critters;
    if (crittersKey) {
      this.critters = new AmbientCrittersSystem(this, CRITTERS_PRESETS[crittersKey]);
    }

    // ── Targeting ────────────────────────────────────────────────────────────
    this.targeting = new TargetingSystem(this, this.player, this.pets, this.destructibles);
    this.targeting.start();

    // ── Save: ładuje aktualny stan monet ─────────────────────────────────────
    // Save jest gwarantowanie wczytany przed createGame() (main.tsx).
    this.coinTotal = saveManager.getCurrent().currencies.coins;

    // ── External sync: modale (sklep jajek, sprzedaż) ustawiają nową wartość ─
    // Bez tego Phaser by trzymał stare coinTotal po kupnie w UI → następny
    // pickup by inkrementował od złej bazy.
    const offCoinsExternal = eventBus.on('coins:setExternal', (e) => {
      this.coinTotal = e.newAmount;
      eventBus.emit('coins:changed', { newAmount: this.coinTotal });
    });

    // ── Active scene + wejście na plac (5.B.3) ───────────────────────────────
    useGameStore.getState().setActiveScene('world');
    const offPlazaEnter = eventBus.on('plaza:enter', () => {
      // Widowiskowy portal zamiast natychmiastowego cięcia — `startTeleport`
      // launchuje TeleportScene (overlay), a podmianę sceny robi w szczycie
      // zasłonięcia. Złoty akcent = vibe placu handlowego.
      startTeleport(this, { target: 'PlazaScene', accent: PLAZA_ACCENT });
    });

    // ── Wejście do Sanktuarium Strażnika (ATL-2b, tylko w biomie Atlantyda) ──
    // Scena = OSTATNIA warstwa walidacji (event może przyjść nie tylko z przycisku
    // React). Gate na realnym save: bez Atlantydy NIE teleportuj — inaczej arena
    // wystartowałaby spoza biomu, a boss-API (ATL-2b.B/C) wpadałoby w 403 (review MED 1).
    const offSanctumEnter = eventBus.on('sanctum:enter', () => {
      if (saveManager.getCurrent().currentBiomeId !== 'biome-atlantis') return;
      startTeleport(this, { target: 'AtlantisSanctumScene', accent: biomeAccent('biome-atlantis') });
    });

    // ── Gems earned (5.0 polish): FX przy graczu gdy world tick dolicza 💎 ──
    // Drop kryształów rollowany server-side → wykrywany jako delta w
    // AutoSaveBridge. Czysto wizualne (kredyt już server-authoritative).
    const offGemsEarned = eventBus.on('gems:earned', (e) => {
      if (this.player) spawnGemPickup(this, this.player.x, this.player.y, e.amount);
    });

    // ── Equipped pets change: UI (equip/unequip/merge) → respawn Phaser pets ─
    // Sesja 22: dynamic equip flow. Zmieniają się uids w save.equipped, listener
    // niszczy aktualne pety i tworzy nowe na podstawie save'a. Targeting system
    // ma nową listę automatycznie (trzyma ref do this.pets, nie kopię).
    const offEquipChanged = eventBus.on('pets:equipChanged', () => {
      const token = ++this.equipAssetSeq;
      void assetBundleManager.ensureCurrentPlayerAssets(this, saveManager.getCurrent()).then((prepared) => {
        if (
          (!this.scene.isActive() && !this.scene.isPaused())
          || token !== this.equipAssetSeq
        ) return;
        this.respawnEquippedPets();
        this.recalcPower();
        assetBundleManager.finalizePreparedScope(prepared, this.textures, this.anims);
      });
    });

    // ── Upgrade purchase: live re-apply runtime hooks (sesja 24b) ────────────
    // Gracz kupił upgrade w UpgradesModal → applyUpgradesFromSave czyta świeży
    // save i nakłada: speed na player'a, mnożnik magnesu jako snapshot.
    // coin_mult / egg_luck są live-read w punkcie użycia, nie wymagają sync'u tu.
    const offUpgradePurchased = eventBus.on('upgrade:purchased', () => {
      this.applyUpgradesFromSave();
    });

    // ── Biom edukacyjny (Świat Flag): stworek-flaga „mówi" zwrot na żądanie ──
    // React EduInteractBar emituje gotowy tekst (native + pl). Dymek pojawia się
    // WYŁĄCZNIE nad ZAŁOŻONYM petem-flagą o tym defId (musi być w świecie, obok
    // gracza). Pet nie założony → nie „mówi" (EduInteractBar i tak pokazuje tylko
    // założone flagi — to dodatkowy bezpiecznik, bez fallbacku nad graczem).
    const offEduSay = eventBus.on('edu:say', ({ petDefId, native, pl }) => {
      const pet = this.pets.find((p) => p.def.id === petDefId);
      if (!pet) return;
      this.eduBubbles.push({ petDefId, bubble: showEduBubble(this, pet.sprite.x, pet.sprite.y, native, pl) });
    });

    const offEventDailyClaimed = eventBus.on('eventDaily:claimed', ({ eventId }) => {
      if (this.eventDailyNpc?.eventId !== eventId) return;
      this.showEventDailyNpcSpeech('Miłego plażowania!');
      this.refreshEventDailyNpcMarker(true);
    });

    // ── Biome switch: full scene restart (sesja 26 dev switch) ───────────────
    // Dev button w App.tsx zmienia save.currentBiomeId i emituje ten event.
    // Pełen restart sceny jest jedynym pewnym sposobem żeby przeładować ALL —
    // tło, dekoracje, destruktyble pozycji, listę monet — od zera. SHUTDOWN
    // handler scleanupuje wszystko, create() startuje fresh z nowego biomu.
    //
    // Sesja 29A review fix: QUEUE pattern zamiast drop'owania eventu podczas
    // aktywnej HatchScene. Bez tego external/delayed `biome:changed` (np.
    // server-side w przyszłości) byłby silent-skipped — save by się zmienił
    // ale scena nie. Teraz zapisujemy w `pendingBiomeChange` i aplikujemy
    // po `hatch:closed`.
    const offBiomeChanged = eventBus.on('biome:changed', (payload) => {
      if (this.scene.isActive('HatchScene')) {
        console.info('[WorldScene] biome:changed odłożone — HatchScene aktywna, restart po hatch:closed.');
        this.pendingBiomeChange = payload.biomeId;
        return;
      }
      // Portal teleportu (overlay) → restart WorldScene w szczycie zasłonięcia
      // (cel = ta sama scena → świeży start z nowym biomem z save). Akcent =
      // kolor docelowego biomu (zielona łąka / szare góry / piaskowy Tatooine).
      startTeleport(this, { target: 'WorldScene', accent: biomeAccent(payload.biomeId) });
    });

    // ── Hatch cinematic launch (sesja 29 — Step A) ───────────────────────────
    // React (EggInventoryModal po roll'u → setHatchResult) emituje 'hatch:start'
    // z gotowym payloadem. WorldScene launchuje HatchScene jako overlay
    // (równoległą scenę). HatchScene odgrywa cinematic, emituje 'hatch:complete'
    // gdy skończy — i wtedy React przechodzi do showResult phase.
    const offHatchStart = eventBus.on('hatch:start', (payload) => {
      // Defensive: jeśli HatchScene już biegnie (poprzedni hatch nie skończony),
      // zatrzymaj ją przed launchem nowej. Bez tego dwa hatchowania w szybkim
      // ciągu (multi-hatch w przyszłości) zostawiłyby zombie scenę.
      if (this.scene.isActive('HatchScene')) {
        this.scene.stop('HatchScene');
      }
      this.scene.launch('HatchScene', payload);
      this.scene.bringToTop('HatchScene');
    });

    // 5.D.3c — bulk hatch cinematic. Ten sam launch flow co single, tylko inny
    // payload shape (`pets[]` zamiast pojedynczego peta). HatchScene dispatcher
    // routuje do HatchBulkDirector.
    const offHatchStartBulk = eventBus.on('hatch:start-bulk', (payload) => {
      if (this.scene.isActive('HatchScene')) {
        this.scene.stop('HatchScene');
      }
      this.scene.launch('HatchScene', payload);
      this.scene.bringToTop('HatchScene');
    });

    // ── Apply pending biome change po hatch:closed (sesja 29A review fix) ────
    // Jeśli podczas hatcha przyszedł `biome:changed`, save się już zmienił ale
    // scena nie zrestartowała (HatchScene aktywna). Po `hatch:closed`
    // (gracz zamknął finalny panel) aplikujemy odkładony switch.
    const offHatchClosed = eventBus.on('hatch:closed', () => {
      if (this.pendingBiomeChange !== null) {
        const queued = this.pendingBiomeChange;
        this.pendingBiomeChange = null;
        console.info(`[WorldScene] aplikuję odłożony biome:changed → ${queued}`);
        startTeleport(this, { target: 'WorldScene', accent: biomeAccent(queued) });
      }
    });

    // ── HUD init ─────────────────────────────────────────────────────────────
    eventBus.emit('coins:changed', { newAmount: this.coinTotal });
    eventBus.emit('power:changed', { newAmount: this.power });
    eventBus.emit('goldRainbowBonus:changed', { bonusPct: Math.round((this.goldRainbowCoinMult - 1) * 100) });

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      offCoinsExternal();
      offGemsEarned();
      offPlazaEnter();
      offSanctumEnter();
      offEquipChanged();
      offUpgradePurchased();
      offEduSay();
      offEventDailyClaimed();
      offBiomeChanged();
      offHatchStart();
      offHatchStartBulk();
      offHatchClosed();
      this.cleanup();
    });

    const decorationCount = biome.decorations?.reduce((a, d) => a + (d.tileStrip ? 1 : d.positions?.length ?? 0), 0) ?? 0;
    console.info(`[WorldScene] biome=${biome.id}, destructibles=${this.destructibles.length}, pets=${this.pets.length}, decorations=${decorationCount}`);
  }

  /**
   * Pełen re-spawn equipped petów ze świeżego `save.equipped` + `save.inventory`.
   *
   * Wywoływane w:
   *  - `create()` jednorazowo przy starcie sceny
   *  - listener `pets:equipChanged` — gdy UI (equip/unequip/merge) zmieniło save
   *
   * Destroy → spawn cycle (nie reuse) bo:
   *  - PetAnimator trzyma własny shadow GameObject — niszczymy razem
   *  - Targeting attackers może mieć ref do peta — release przed destroy
   *  - Slot indexes się przeliczają (3 → 4 → 5 → 6) dynamicznie
   *
   * Edge case: equipped uid spoza inventory (np. po merge — pet skonsumowany
   * ale uid wciąż w equipped) → defensywne filter `inventory.find()`. Recipe
   * w UI powinno usunąć z equipped razem, ale to dodatkowa warstwa safety.
   */
  private respawnEquippedPets(): void {
    // 1. Destroy aktualne pety przez Pet.destroy() — sprząta sprite, animator,
    //    aurę i wszystkie tweens (sesja 23: cleanup scentralizowany w Pet).
    for (const pet of this.pets) {
      pet.destroy();
    }
    // ⚠ Mutate-in-place (length=0), NIE reassign (`this.pets = []`).
    // TargetingSystem dostał ref do tej tablicy w `new TargetingSystem(..., this.pets, ...)`.
    // Reassign zostawiłby targeting z STARĄ tablicą (= 0 petów na zawsze).
    this.pets.length = 0;

    // 2. Clear attacker sets w destructibles (pety zniknęły, nie mogą atakować)
    for (const dest of this.destructibles) {
      dest.attackers.clear();
    }

    // 3. Czytaj save fresh — `pets:equipChanged` może być wywołane wielokrotnie
    const save = saveManager.getCurrent();
    const equippedUids = save.equipped;
    const inventory = save.inventory;

    // 4. Spawn nowe pety z save.equipped — defensywne filter undefined
    const equippedInstances: PetInstance[] = [];
    for (const uid of equippedUids) {
      const instance = inventory.find((p) => p.uid === uid);
      if (instance) equippedInstances.push(instance);
      else console.warn(`[WorldScene] Equipped uid ${uid} not in inventory — skipping`);
    }

    const slotCount = equippedInstances.length;
    for (let i = 0; i < equippedInstances.length; i++) {
      const instance = equippedInstances[i]!;
      const def = PETS_BY_ID[instance.defId];
      if (!def) {
        console.warn(`[WorldScene] Unknown pet defId: ${instance.defId} — skipping`);
        continue;
      }
      const pet = new Pet(this, def, instance, i, slotCount, this.player);
      this.pets.push(pet);
    }

    // Targeting trzyma ref do `this.pets` — nie trzeba re-init, kolejny tick wykryje nowe.
    console.info(`[WorldScene] Equipped pets respawned: ${this.pets.length}/${equippedUids.length}`);
  }

  private recalcPower(): void {
    this.power = Math.round(this.pets.reduce((acc, p) => acc + p.power, 0));
    eventBus.emit('power:changed', { newAmount: this.power });
    // Bonus złota z petów gold/rainbow zależy od TEGO samego składu drużyny — przeliczamy
    // razem, żeby żaden call site recalcPower nie zapomniał odświeżyć bonusu.
    this.recalcGoldRainbowCoinBonus();
  }

  /**
   * Przelicza mnożnik złota z equipped petów gold/rainbow (suma `Pet.coinBonus`).
   * Emituje `goldRainbowBonus:changed` (procent) do HUD. Wołane z `recalcPower`.
   */
  private recalcGoldRainbowCoinBonus(): void {
    const bonus = this.pets.reduce((acc, p) => acc + p.coinBonus, 0);
    this.goldRainbowCoinMult = 1 + bonus;
    eventBus.emit('goldRainbowBonus:changed', { bonusPct: Math.round(bonus * 100) });
  }

  /**
   * Czyta `save.upgrades` i aplikuje runtime efekty (sesja 24b).
   *
   * Wywoływane w `create()` raz na start sceny oraz przez listener
   * `upgrade:purchased` po każdym zakupie w UpgradesModal.
   *
   * Co tu sync'ujemy:
   *  - `walk_speed` → player.setSpeedFromWalkSpeedLevel() (live na sprite)
   *  - `magnet_range` → snapshot `currentMagnetMult` używany przy spawnie monet
   *
   * Co NIE wymaga tu sync'u:
   *  - `coin_mult` → live read w `onDestructibleDestroyed` (jednorazowa konsumpcja)
   *  - `egg_luck` → live read w EggInventoryModal recipe
   *  - `pet_slots`/`pet_storage`/`egg_storage` → UI cap helpers same się odświeżają
   */
  private applyUpgradesFromSave(): void {
    const upgrades = saveManager.getCurrent().upgrades;
    this.player.setSpeedFromWalkSpeedLevel(upgrades['walk_speed'] ?? 0);
    this.currentMagnetMult = magnetMult(upgrades['magnet_range'] ?? 0);
  }

  update(time: number, deltaMs: number): void {
    const dt = deltaMs / 1000;

    this.player.update(time, dt);
    this.enforcePlayerMovementRules(time);

    const petConstraint = this.movementRules?.blockedWater ? this.petPositionConstraint : undefined;
    for (const pet of this.pets) {
      pet.update(dt, time, petConstraint);
    }
    for (const dest of this.destructibles) {
      dest.tick(time);
    }
    for (const coin of this.coins) {
      coin.tick(time, dt);
    }
    this.coins = this.coins.filter((c) => !!c.sprite.scene);

    // ── Dymki edukacyjne: śledź pozycję swojego peta, usuń wygasłe ──────────
    if (this.eduBubbles.length > 0) {
      const now = this.time.now;
      this.eduBubbles = this.eduBubbles.filter(({ petDefId, bubble }) => {
        if (now >= bubble.expiresAt) return false; // kontener sam zniknął (fadeOut onComplete)
        const pet = this.pets.find((p) => p.def.id === petDefId);
        if (pet) bubble.update(pet.sprite.x, pet.sprite.y);
        return true;
      });
    }

    this.updateEventDailyNpc(time);

    // ── Dekoracje: sway + touch push + sparkle ──────────────────────────────
    if (this.decorationAnimators.length > 0) {
      // Wypełnij bufor entity positions (player + pety) — reuse zamiast alokacji
      this.entityBuffer.length = 0;
      this.entityBuffer.push({ x: this.player.x, y: this.player.y });
      for (const pet of this.pets) {
        this.entityBuffer.push({ x: pet.sprite.x, y: pet.sprite.y });
      }
      for (const deco of this.decorationAnimators) {
        deco.update(time, this.entityBuffer);
      }
    }

    // Tylko jedna otwarta książka przewraca stronę naraz; system sam losuje
    // przerwy i obsługuje tapnięcie konkretnej książki.
    this.bookPageFlipSystem?.update(time);

    // ── Ambient FX (padający śnieg itd.) — Step 3.B.2 ───────────────────────
    // Sam pilnuje sobie emitter position relative do kamery + wind oscillation
    // + rotation alive particles. null jeśli biom nie ma ambient.
    this.ambientFx?.update(time, dt);

    // ── Atmosphere overlay — przelicza pozycje pod aktualny viewport
    //    (resize-safe) + pulsy/sway. null jeśli biom nie ma atmosfery.
    this.atmosphere?.update(time);

    // ── Parallax tła — ustawia tilePosition z scrolla kamery + autodryf.
    //    null jeśli biom nie ma parallaxu.
    this.parallax?.update(time);

    // ── Ambient critters — spawn/tick/despawn stworzonek. null jeśli brak.
    //    Pozycja gracza → stworzonka z cfg.flee uciekają przed nim.
    this.critters?.update(time, dt, this.player.x, this.player.y);
  }

  private enforcePlayerMovementRules(time: number): void {
    const projection = this.projectWalkablePosition(this.player.x, this.player.y);
    if (!projection.corrected) return;

    this.player.sprite.body.reset(projection.x, projection.y);
    this.player.sprite.setPosition(projection.x, projection.y);
    this.spawnWaterBoundarySplash(projection.x, projection.y, time);
  }

  private projectWalkablePosition(x: number, y: number): WalkProjection {
    const water = this.movementRules?.blockedWater;
    if (!water || y >= water.shoreY) {
      return { x, y, corrected: false };
    }

    const walkways = water.walkways ?? [];
    for (const walkway of walkways) {
      if (x < walkway.xMin || x > walkway.xMax) continue;
      const yMax = walkway.yMax ?? water.shoreY;
      if (y >= walkway.yMin && y <= yMax) {
        return { x, y, corrected: false };
      }
      if (y < walkway.yMin) {
        return { x, y: walkway.yMin, corrected: true };
      }
    }

    const sideTolerance = 18;
    for (const walkway of walkways) {
      const yMax = walkway.yMax ?? water.shoreY;
      const nearWalkwaySide = x >= walkway.xMin - sideTolerance && x <= walkway.xMax + sideTolerance;
      if (nearWalkwaySide && y >= walkway.yMin && y <= yMax) {
        return {
          x: Phaser.Math.Clamp(x, walkway.xMin, walkway.xMax),
          y,
          corrected: true,
        };
      }
    }

    return { x, y: water.shoreY, corrected: true };
  }

  private spawnWaterBoundarySplash(x: number, y: number, time: number): void {
    const water = this.movementRules?.blockedWater;
    if (!water || time < this.nextWaterSplashAt) return;
    this.nextWaterSplashAt = time + (water.splashCooldownMs ?? 500);

    const textureKey = water.splashTextureKey;
    const splashY = Math.min(y, water.shoreY) - 10;
    if (textureKey && this.textures.exists(textureKey)) {
      const splash = this.add.image(x, splashY, textureKey)
        .setDisplaySize(88, 39)
        .setAlpha(0.9)
        .setDepth(36)
        .setRotation(Phaser.Math.FloatBetween(-0.12, 0.12));
      this.tweens.add({
        targets: splash,
        y: splash.y - 8,
        scaleX: splash.scaleX * 1.18,
        scaleY: splash.scaleY * 1.18,
        alpha: 0,
        duration: 430,
        ease: 'Sine.easeOut',
        onComplete: () => splash.destroy(),
      });
      return;
    }

    const ripple = this.add.ellipse(x, splashY, 64, 22, 0xffffff, 0)
      .setStrokeStyle(3, 0xd9ffff, 0.72)
      .setDepth(36);
    this.tweens.add({
      targets: ripple,
      scaleX: 1.35,
      scaleY: 1.2,
      alpha: 0,
      duration: 360,
      ease: 'Sine.easeOut',
      onComplete: () => ripple.destroy(),
    });
  }

  private installInflatableCapybaraInteraction(sprite: Phaser.GameObjects.Image): void {
    sprite
      .setData('baseScaleX', sprite.scaleX)
      .setData('baseScaleY', sprite.scaleY)
      .setInteractive({ useHandCursor: true });

    sprite.on('pointerdown', (
      _pointer: Phaser.Input.Pointer,
      _localX: number,
      _localY: number,
      event: Phaser.Types.Input.EventData,
    ) => {
      event.stopPropagation();
      this.playInflatableCapybaraTapFx(sprite);
    });
  }

  private playInflatableCapybaraTapFx(sprite: Phaser.GameObjects.Image): void {
    if (!sprite.scene || this.time.now < this.nextInflatableCapybaraFxAt) return;
    this.nextInflatableCapybaraFxAt = this.time.now + 900;

    const baseScaleX = Number(sprite.getData('baseScaleX')) || sprite.scaleX;
    const baseScaleY = Number(sprite.getData('baseScaleY')) || sprite.scaleY;
    sprite.setScale(baseScaleX, baseScaleY);
    this.tweens.add({
      targets: sprite,
      scaleX: baseScaleX * 1.08,
      scaleY: baseScaleY * 0.91,
      duration: 105,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
      onComplete: () => sprite.setScale(baseScaleX, baseScaleY),
    });

    const fxDepth = sprite.depth + 2;
    const splashX = sprite.x + Phaser.Math.Between(-12, 16);
    const splashY = sprite.y + 62;
    if (this.textures.exists('deco_beach_water_splash')) {
      const splash = this.add.image(splashX, splashY, 'deco_beach_water_splash')
        .setDisplaySize(126, 56)
        .setAlpha(0.82)
        .setDepth(fxDepth)
        .setRotation(Phaser.Math.FloatBetween(-0.18, 0.18));
      this.tweens.add({
        targets: splash,
        scaleX: splash.scaleX * 1.16,
        scaleY: splash.scaleY * 1.16,
        alpha: 0,
        duration: 520,
        ease: 'Sine.easeOut',
        onComplete: () => splash.destroy(),
      });
    }

    for (let i = 0; i < 6; i++) {
      const bubble = this.add.circle(
        sprite.x + Phaser.Math.Between(-96, 96),
        sprite.y + Phaser.Math.Between(38, 76),
        Phaser.Math.Between(4, 8),
        0xf0ffff,
        0.72,
      ).setStrokeStyle(2, 0x82d7e9, 0.48).setDepth(fxDepth + 1);
      this.tweens.add({
        targets: bubble,
        y: bubble.y - Phaser.Math.Between(24, 46),
        x: bubble.x + Phaser.Math.Between(-10, 10),
        scaleX: 1.35,
        scaleY: 1.35,
        alpha: 0,
        delay: i * 45,
        duration: 520 + i * 35,
        ease: 'Sine.easeOut',
        onComplete: () => bubble.destroy(),
      });
    }
  }

  private installEventFishInteraction(sprite: Phaser.GameObjects.Image): void {
    sprite
      .setData('baseAlpha', sprite.alpha)
      .setData('baseScaleX', sprite.scaleX)
      .setData('baseScaleY', sprite.scaleY)
      .setInteractive({ useHandCursor: true });

    sprite.on('pointerdown', (
      _pointer: Phaser.Input.Pointer,
      _localX: number,
      _localY: number,
      event: Phaser.Types.Input.EventData,
    ) => {
      event.stopPropagation();
      this.playEventFishTapFx(sprite);
    });
  }

  private playEventFishTapFx(sprite: Phaser.GameObjects.Image): void {
    if (!sprite.scene) return;
    const nextFxAt = Number(sprite.getData('nextFishFxAt')) || 0;
    if (this.time.now < nextFxAt) return;
    sprite.setData('nextFishFxAt', this.time.now + 2100);

    const floatTween = sprite.getData('floatTween') as Phaser.Tweens.Tween | undefined;
    floatTween?.pause();

    const baseAlpha = Number(sprite.getData('baseAlpha')) || sprite.alpha;
    const baseScaleX = Number(sprite.getData('baseScaleX')) || sprite.scaleX;
    const baseScaleY = Number(sprite.getData('baseScaleY')) || sprite.scaleY;
    const baseRotation = sprite.rotation;
    const fxX = sprite.x;
    const fxY = sprite.y - 16;
    const fxDepth = -55;
    sprite.setVisible(true).setAlpha(baseAlpha).setScale(baseScaleX, baseScaleY);

    for (let i = 0; i < 3; i++) {
      const ring = this.add.ellipse(
        fxX,
        fxY,
        118 + i * 32,
        58 + i * 18,
        0xffffff,
        0,
      ).setStrokeStyle(4 - i, 0xbff8ff, 0.46).setDepth(fxDepth).setRotation(Phaser.Math.DegToRad(i * 28));
      this.tweens.add({
        targets: ring,
        rotation: ring.rotation + Math.PI * 1.18,
        scaleX: 1.18,
        scaleY: 1.18,
        alpha: 0,
        duration: 780 + i * 150,
        ease: 'Sine.easeOut',
        onComplete: () => ring.destroy(),
      });
    }

    for (let i = 0; i < 7; i++) {
      const bubble = this.add.circle(
        fxX + Phaser.Math.Between(-82, 82),
        fxY + Phaser.Math.Between(-36, 42),
        Phaser.Math.Between(3, 7),
        0xf0ffff,
        0.62,
      ).setStrokeStyle(2, 0x8ee8f2, 0.42).setDepth(fxDepth + 1);
      this.tweens.add({
        targets: bubble,
        y: bubble.y - Phaser.Math.Between(20, 42),
        x: bubble.x + Phaser.Math.Between(-16, 16),
        scaleX: 1.4,
        scaleY: 1.4,
        alpha: 0,
        delay: i * 35,
        duration: 660 + i * 45,
        ease: 'Sine.easeOut',
        onComplete: () => bubble.destroy(),
      });
    }

    this.tweens.add({
      targets: sprite,
      rotation: baseRotation + Math.PI * 2,
      scaleX: baseScaleX * 0.72,
      scaleY: baseScaleY * 0.72,
      alpha: 0.08,
      duration: 760,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        sprite.setVisible(false).setRotation(baseRotation).setScale(baseScaleX * 0.8, baseScaleY * 0.8);
        this.time.delayedCall(640, () => {
          if (!sprite.scene) return;
          sprite.setVisible(true).setAlpha(0).setRotation(baseRotation);
          this.tweens.add({
            targets: sprite,
            scaleX: baseScaleX,
            scaleY: baseScaleY,
            alpha: baseAlpha,
            duration: 680,
            ease: 'Sine.easeOut',
            onComplete: () => {
              sprite.setScale(baseScaleX, baseScaleY).setAlpha(baseAlpha);
              floatTween?.resume();
            },
          });
        });
      },
    });
  }

  private onDestructibleDestroyed(dest: Destructible): void {
    spawnDestructionFX(this, dest.x, dest.y, dest.def.art.destroyFx);
    shakeForDestructible(this, dest.def.id, dest.def.art.destroyFx);
    Haptics.destructibleDestroyed();

    const rewards = dest.rollRewards();
    // Live read upgrade'u coin_mult — gracz kupił boost w trakcie walki?
    // Następna destrukcja od razu skorzysta. (Sesja 24b.)
    const coinMult = coinGainMult(saveManager.getCurrent().upgrades['coin_mult'] ?? 0);
    // Live read aktywnego buffa złota z mikstury (1 gdy brak/wygasł). Czytane
    // raz na destrukcję — kolejne niszczenie od razu uwzględni świeżo użytą miksturę.
    const goldPotionMult = activeEffectMult(
      saveManager.getCurrent().activeEffects,
      'gold',
      Date.now(),
    );
    // Bonus złota z petów gold/rainbow — snapshot z `recalcGoldRainbowCoinBonus`,
    // odświeżany przy każdej zmianie drużyny. Mnoży się z coin_mult.
    for (const r of rewards) {
      if (r.currency !== 'coins') continue;
      const totalCoins = Math.round(
        r.amount * coinMult * this.goldRainbowCoinMult * goldPotionMult,
      );
      const nSprites = coinSpriteCount(totalCoins);
      const valuePer = Math.ceil(totalCoins / nSprites);
      let remaining = totalCoins;
      for (let i = 0; i < nSprites; i++) {
        const v = Math.min(valuePer, remaining);
        remaining -= v;
        if (v <= 0) break;
        const coin = new Coin(
          this, dest.x, dest.y, v, this.player,
          (val) => this.addCoins(val),
          this.currentMagnetMult,
        );
        this.coins.push(coin);
      }
    }
    eventBus.emit('destructible:destroyed', { destructibleId: dest.def.id });
  }

  private addCoins(amount: number): void {
    this.coinTotal = addCoins(this.coinTotal, amount);
    eventBus.emit('coins:changed', { newAmount: this.coinTotal });
    // Delta dla AutoSaveBridge → batch do `POST /world/tick` (sesja 4.D.3).
    // `coins:changed` niesie total i odpala się też przy `coins:setExternal`
    // (sync po akcji ekonomii) — bridge musi liczyć WYŁĄCZNIE realny zarobek.
    eventBus.emit('coins:earned', { amount });
  }

  private spawnEventDailyTraderNpc(biomeId: string): void {
    const eventDef = getEventByBiomeId(biomeId);
    if (!eventDef?.dailyReward) return;

    const x = eventDef.dailyNpc?.x ?? 585;
    const y = eventDef.dailyNpc?.y ?? 1115;
    this.ensureEventDailyNpcAnims();
    const animated = this.anims.exists('event_daily_npc_idle_a') && this.anims.exists('event_daily_npc_idle_b');
    this.add.ellipse(x, y - 8, 92, 30, 0x5c3b1e, 0.18).setDepth(-50.5);
    const sprite: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image = animated
      ? this.add.sprite(x, y, 'plaza_npc_sheet_a').setOrigin(0.5, 1)
      : this.add.image(x, y, this.textures.exists('plaza_npc_seller') ? 'plaza_npc_seller' : 'placeholder-square').setOrigin(0.5, 1);

    const maxSide = Math.max(sprite.width, sprite.height, 1);
    sprite.setScale(166 / maxSide).setDepth(-50);
    if (sprite instanceof Phaser.GameObjects.Sprite) {
      sprite.play('event_daily_npc_idle_a');
      sprite.on(Phaser.Animations.Events.ANIMATION_COMPLETE, () => {
        const next = sprite.anims.currentAnim?.key === 'event_daily_npc_idle_a'
          ? 'event_daily_npc_idle_b'
          : 'event_daily_npc_idle_a';
        if (this.anims.exists(next)) sprite.play(next);
      });
    }

    const hitZone = this.add
      .zone(x - 70, y - 98, 310, 260)
      .setOrigin(0.5)
      .setDepth(100_300)
      .setInteractive({ useHandCursor: true });
    this.worldInteractions.bind(hitZone, () => this.openEventDailyRewardModal());

    const arrow = this.textures.exists('ui_arrow_tutorial')
      ? this.add.image(x - 92, y - 220, 'ui_arrow_tutorial').setDisplaySize(58, 58).setAlpha(0.92).setDepth(100_200)
      : undefined;
    if (arrow) {
      this.tweens.add({
        targets: arrow,
        y: arrow.y - 10,
        duration: 650,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    const marker = new WorldInteractionMarker(
      this,
      x + 44,
      y - 178,
      () => this.openEventDailyRewardModal(),
      this.worldInteractions,
    );

    this.eventDailyNpc = {
      eventId: eventDef.id,
      x,
      y,
      sprite,
      hitZone,
      ...(arrow && { arrow }),
      marker,
      nextAvailabilityCheckAt: 0,
    };
    this.refreshEventDailyNpcMarker(true);
  }

  private ensureEventDailyNpcAnims(): void {
    const mk = (key: string, sheet: string): void => {
      if (this.anims.exists(key) || !this.textures.exists(sheet)) return;
      this.anims.create({
        key,
        frames: this.anims.generateFrameNumbers(sheet, { start: 0, end: 24 }),
        frameRate: 9,
        repeat: 0,
      });
    };
    mk('event_daily_npc_idle_a', 'plaza_npc_sheet_a');
    mk('event_daily_npc_idle_b', 'plaza_npc_sheet_b');
  }

  private openEventDailyRewardModal(): void {
    const npc = this.eventDailyNpc;
    if (!npc) return;
    const store = useGameStore.getState();
    if (!canUseWorldInteraction(store)) return;

    const available = this.isEventDailyRewardLocallyAvailable(npc.eventId);
    this.showEventDailyNpcSpeech(
      available ? (getEventById(npc.eventId)?.dailyNpc?.availableSpeech ?? 'Mam dziś paczkę z plaży!') : 'Wróć jutro po kolejną paczkę!',
    );
    store.setOpenModal('eventDailyReward');
  }

  private isEventDailyRewardLocallyAvailable(eventId: string): boolean {
    if (!isEventActive(eventId)) return false;
    const today = new Date().toISOString().slice(0, 10);
    const daily = saveManager.getCurrent().events?.[eventId]?.dailyTrader;
    return daily?.lastClaimDay !== today;
  }

  private showEventDailyNpcSpeech(textValue: string): void {
    const npc = this.eventDailyNpc;
    if (!npc) return;
    npc.speech?.destroy();
    npc.speech = showNpcSpeech(this, npc.x, npc.y - 225, textValue);
  }

  private refreshEventDailyNpcMarker(force = false): void {
    const npc = this.eventDailyNpc;
    if (!npc) return;
    if (!force && this.time.now < npc.nextAvailabilityCheckAt) return;
    npc.nextAvailabilityCheckAt = this.time.now + 1000;
    const visible = this.isEventDailyRewardLocallyAvailable(npc.eventId);
    npc.arrow?.setVisible(visible);
    npc.marker.setAttention(visible);
  }

  private updateEventDailyNpc(time: number): void {
    const npc = this.eventDailyNpc;
    if (!npc) return;
    this.refreshEventDailyNpcMarker();
    if (npc.speech) {
      if (time >= npc.speech.expiresAt) {
        npc.speech = undefined;
      } else {
        npc.speech.update(npc.x, npc.y - 225);
      }
    }
  }

  private spawnEventPierFishingSpot(biomeId: string): void {
    const eventDef = getEventByBiomeId(biomeId);
    if (!eventDef?.pierFishing) return;

    const x = 1100;
    const y = 240;
    const textureKey = this.textures.exists('deco_beach_pier_fishing_spot')
      ? 'deco_beach_pier_fishing_spot'
      : 'placeholder-square';

    this.add.ellipse(x, y + 22, 98, 34, 0x0b4963, 0.18).setDepth(-44.5);
    const sprite = this.add.image(x, y, textureKey)
      .setDisplaySize(112, 109)
      .setDepth(-44);
    this.tweens.add({
      targets: sprite,
      y: y + 7,
      rotation: Phaser.Math.DegToRad(2),
      duration: 1600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const hitZone = this.add
      .zone(x, y, 190, 170)
      .setOrigin(0.5)
      .setDepth(100_310)
      .setInteractive({ useHandCursor: true });
    const interactionOptions = {
      completionFeedback: () => this.isEventPierFishingReady()
        ? 'success' as const
        : 'neutral' as const,
    };
    this.worldInteractions.bind(
      hitZone,
      () => { void this.claimEventPierFishing(); },
      interactionOptions,
    );

    const marker = new WorldInteractionMarker(
      this,
      x + 54,
      y - 70,
      () => { void this.claimEventPierFishing(); },
      this.worldInteractions,
      interactionOptions,
    );

    const arrow = this.textures.exists('ui_arrow_tutorial')
      ? this.add.image(x - 78, y - 88, 'ui_arrow_tutorial').setDisplaySize(52, 52).setAlpha(0.9).setDepth(100_205)
      : undefined;
    if (arrow) {
      this.tweens.add({
        targets: arrow,
        y: arrow.y - 9,
        duration: 680,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    this.eventPierFishing = {
      eventId: eventDef.id,
      x,
      y,
      sprite,
      hitZone,
      marker,
      ...(arrow && { arrow }),
      isClaiming: false,
      nextAvailabilityCheckAt: 0,
    };
    this.refreshEventPierFishingMarker(true);
  }

  private isEventPierFishingLocallyAvailable(eventId: string): boolean {
    if (!isEventActive(eventId)) return false;
    const eventDef = getEventById(eventId);
    const cooldownMs = eventDef?.pierFishing?.cooldownMs;
    if (!cooldownMs) return false;
    const serverCooldownUntil = this.eventPierFishing?.eventId === eventId
      ? this.eventPierFishing.serverCooldownUntil
      : undefined;
    if (serverCooldownUntil != null && Date.now() < serverCooldownUntil) return false;
    const lastClaimAt = saveManager.getCurrent().events?.[eventId]?.pierFishing?.lastClaimAt;
    return lastClaimAt == null || Date.now() >= lastClaimAt + cooldownMs;
  }

  /** Jedyny test gotowości łowiska — badge i feedback holda muszą się zgadzać. */
  private isEventPierFishingReady(): boolean {
    const spot = this.eventPierFishing;
    return !!spot
      && this.isEventPierFishingLocallyAvailable(spot.eventId)
      && !spot.isClaiming;
  }

  private refreshEventPierFishingMarker(force = false): void {
    const spot = this.eventPierFishing;
    if (!spot) return;
    if (!force && this.time.now < spot.nextAvailabilityCheckAt) return;
    spot.nextAvailabilityCheckAt = this.time.now + 1000;
    const visible = this.isEventPierFishingReady();
    spot.marker.setAttention(visible);
    spot.arrow?.setVisible(visible);
  }

  private async claimEventPierFishing(): Promise<void> {
    const spot = this.eventPierFishing;
    if (!spot || spot.isClaiming) return;
    if (!canUseWorldInteraction(useGameStore.getState())) return;

    if (!this.isEventPierFishingLocallyAvailable(spot.eventId)) {
      this.showPierFishingText('Jeszcze chwilę', 0xfef3c7);
      return;
    }

    spot.isClaiming = true;
    this.refreshEventPierFishingMarker(true);
    this.showPierFishingText('Łowienie...', 0xe0f2fe);
    const castFx = this.playPierFishingCastFx();

    try {
      const result = await runEconomyAction(() => economyApi.claimEventPierFishing(spot.eventId));
      await castFx;
      this.playPierFishingRewardFx(result.shells_received);
      eventBus.emit('eventPierFishing:claimed', {
        eventId: spot.eventId,
        shellsReceived: result.shells_received,
      });
    } catch (err) {
      await castFx;
      const isCooldown = err instanceof ApiError && err.code === 'event_pier_fishing_cooldown';
      if (isCooldown && err.retryAfterMs != null) {
        spot.serverCooldownUntil = Date.now() + err.retryAfterMs;
      }
      // Krótki tekst nad wędką — „Odśwież grę" mówi prawdę: nic nie wyszło na
      // serwer i nie pomoże kolejne zarzucenie (Codex V-4 FIXES⁵ MED 1).
      const isResyncGate = err instanceof ApiError && err.code === ECONOMY_RESYNC_CODE;
      const message = isResyncGate ? 'Odśwież grę' : isCooldown ? 'Wróć później' : 'Nie udało się';
      this.showPierFishingText(message, 0xfff1f2);
    } finally {
      spot.isClaiming = false;
      this.refreshEventPierFishingMarker(true);
    }
  }

  private playPierFishingCastFx(): Promise<void> {
    const spot = this.eventPierFishing;
    if (!spot) return Promise.resolve();

    const rodX = spot.x - 24;
    const rodY = spot.y - 48;
    const hookX = spot.x - 70;
    const hookY = spot.y + 18;
    const line = this.add.graphics().setDepth(37);
    const bobber = this.add.container(rodX, rodY).setDepth(38).setAlpha(0).setScale(0.64);
    const bobberBody = this.add.circle(0, 0, 9, 0xffffff, 1).setStrokeStyle(2, 0x7a3d00, 0.45);
    const bobberTop = this.add.circle(0, -4, 7, 0xff5b4a, 1);
    const bobberDot = this.add.circle(0, 3, 3, 0x6ee7f9, 1);
    bobber.add([bobberBody, bobberTop, bobberDot]);
    const cast = { progress: 0 };

    const drawLine = (): void => {
      const x = Phaser.Math.Linear(rodX, hookX, cast.progress);
      const y = Phaser.Math.Linear(rodY, hookY, cast.progress);
      line.clear();
      line.lineStyle(4, 0x7a4a18, 0.22);
      line.lineBetween(rodX + 2, rodY + 2, x + 2, y + 2);
      line.lineStyle(3, 0xfff4bd, 0.95);
      line.lineBetween(rodX, rodY, x, y);
      bobber.setPosition(x, y);
    };

    this.tweens.add({
      targets: spot.sprite,
      scaleX: spot.sprite.scaleX * 1.08,
      scaleY: spot.sprite.scaleY * 1.08,
      rotation: spot.sprite.rotation + Phaser.Math.DegToRad(-7),
      duration: 220,
      yoyo: true,
      repeat: 2,
      ease: 'Sine.easeInOut',
    });

    this.tweens.add({
      targets: bobber,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      duration: 180,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: cast,
      progress: 1,
      duration: 520,
      ease: 'Cubic.easeOut',
      onUpdate: drawLine,
      onComplete: drawLine,
    });
    this.tweens.add({
      targets: bobber,
      y: hookY + 8,
      rotation: Phaser.Math.DegToRad(-8),
      delay: 620,
      duration: 260,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.easeInOut',
    });

    for (let i = 0; i < 4; i++) {
      const ring = this.add.circle(
        hookX + Phaser.Math.Between(-8, 8),
        hookY + Phaser.Math.Between(-3, 8),
        8,
        0xffffff,
        0,
      ).setStrokeStyle(3, 0xd9ffff, 0.7).setDepth(-43);
      this.tweens.add({
        targets: ring,
        scaleX: 2.2 + i * 0.28,
        scaleY: 1.18 + i * 0.16,
        alpha: 0,
        delay: 520 + i * 310,
        duration: 620 + i * 70,
        ease: 'Sine.easeOut',
        onComplete: () => ring.destroy(),
      });
    }

    return new Promise((resolve) => {
      this.time.delayedCall(1750, () => {
        const splash = this.add.ellipse(hookX, hookY + 4, 30, 12, 0xffffff, 0)
          .setStrokeStyle(4, 0xf0ffff, 0.78)
          .setDepth(39);
        this.tweens.add({
          targets: splash,
          scaleX: 2.2,
          scaleY: 1.65,
          alpha: 0,
          duration: 420,
          ease: 'Sine.easeOut',
          onComplete: () => splash.destroy(),
        });
        this.tweens.add({
          targets: [line, bobber],
          alpha: 0,
          duration: 280,
          ease: 'Sine.easeOut',
          onComplete: () => {
            line.destroy();
            bobber.destroy();
            resolve();
          },
        });
      });
    });
  }

  private playPierFishingRewardFx(shellsReceived: number): void {
    const spot = this.eventPierFishing;
    if (!spot) return;
    Haptics.coinPickup();

    const c = this.add.container(spot.x, spot.y - 58).setDepth(100_420);
    if (this.textures.exists('ui_shell')) {
      const icon = this.add.image(0, 0, 'ui_shell').setDisplaySize(34, 34);
      c.add(icon);
    }
    const txt = this.add.text(27, 1, `+${shellsReceived}`, {
      fontFamily: 'Baloo 2, sans-serif',
      fontSize: '28px',
      fontStyle: 'bold',
      color: '#fff7d6',
      stroke: '#7a3d00',
      strokeThickness: 5,
    }).setOrigin(0, 0.5);
    c.add(txt);

    const startY = c.y;
    this.tweens.add({
      targets: c,
      y: startY - 28,
      scaleX: 1.18,
      scaleY: 1.18,
      duration: 360,
      ease: 'Back.easeOut',
    });
    this.tweens.add({
      targets: c,
      y: startY - 100,
      alpha: 0,
      delay: 1100,
      duration: 850,
      ease: 'Cubic.easeOut',
      onComplete: () => c.destroy(),
    });
  }

  private showPierFishingText(textValue: string, fillColor: number): void {
    const spot = this.eventPierFishing;
    if (!spot) return;
    const c = this.add.container(spot.x, spot.y - 68).setDepth(100_420);
    const bg = this.add.graphics();
    bg.fillStyle(fillColor, 0.94);
    bg.fillRoundedRect(-64, -17, 128, 34, 12);
    bg.lineStyle(2, 0x7a3d00, 0.4);
    bg.strokeRoundedRect(-64, -17, 128, 34, 12);
    const txt = this.add.text(0, 0, textValue, {
      fontFamily: 'Baloo 2, sans-serif',
      fontSize: '17px',
      fontStyle: 'bold',
      color: '#6b3a00',
    }).setOrigin(0.5);
    c.add([bg, txt]);
    const startY = c.y;
    this.tweens.add({
      targets: c,
      y: startY - 16,
      duration: 280,
      ease: 'Sine.easeOut',
    });
    this.tweens.add({
      targets: c,
      y: startY - 54,
      alpha: 0,
      delay: 1300,
      duration: 700,
      ease: 'Sine.easeOut',
      onComplete: () => c.destroy(),
    });
  }

  private cleanup(): void {
    this.inputSystem.detach();
    this.targeting.stop();
    // AmbientFxSystem niszczy własne emittery + ich live particles.
    // Phaser scene.restart() destroyuje GameObjects sam, ale lepiej mieć
    // explicit destroy() żeby był deterministic order (i żeby działało gdyby
    // kiedyś WorldScene żyło dłużej niż jedna scena).
    this.ambientFx?.destroy();
    this.ambientFx = null;
    // AtmosphereSystem niszczy własne overlay GameObjecty (vignette/rays/tint).
    this.atmosphere?.destroy();
    this.atmosphere = null;
    // ParallaxBackgroundSystem niszczy własne TileSprite'y.
    this.parallax?.destroy();
    this.parallax = null;
    // AmbientCrittersSystem niszczy wszystkie żywe stworzonka.
    this.critters?.destroy();
    this.critters = null;
    this.bookPageFlipSystem?.destroy();
    this.bookPageFlipSystem = null;
    // Dymki edukacyjne (Świat Flag) — usuń ewentualne wiszące przy zmianie biomu.
    for (const { bubble } of this.eduBubbles) bubble.destroy();
    this.eduBubbles.length = 0;
    this.eventDailyNpc?.speech?.destroy();
    this.eventDailyNpc?.marker.destroy();
    this.eventDailyNpc = null;
    this.eventPierFishing?.marker.destroy();
    this.eventPierFishing = null;
  }

  /** Fallback placeholder gdy prawdziwa tekstura destructibla nie istnieje.
   *  Mountain destruktyble (sesja 26) mapowane na te same shape-y co meadow
   *  — coin pile S/M/L → placeholder-coin-pile-S/M/L itd. — bo rozmiary są
   *  identyczne, tylko skin (snow vs wood/stone) byłby inny. */
  private fallbackPlaceholderFor(destId: string): string {
    switch (destId) {
      // meadow
      case 'dest-coin-pile-s': return 'placeholder-coin-pile-s';
      case 'dest-coin-pile-m': return 'placeholder-coin-pile-m';
      case 'dest-coin-pile-l': return 'placeholder-coin-pile-l';
      case 'dest-chest-wood':  return 'placeholder-chest-wood';
      case 'dest-chest-stone': return 'placeholder-chest-stone';
      case 'dest-crystal':     return 'placeholder-crystal';
      // mountain (sesja 26) — reuse meadow placeholders (rozmiar = identyczny)
      case 'dest-snow-coin-pile-s':   return 'placeholder-coin-pile-s';
      case 'dest-snow-coin-pile-m':   return 'placeholder-coin-pile-m';
      case 'dest-snow-coin-pile-l':   return 'placeholder-coin-pile-l';
      case 'dest-mountain-rock-chest': return 'placeholder-chest-wood';
      case 'dest-ice-chest':          return 'placeholder-chest-stone';
      case 'dest-ice-crystal':        return 'placeholder-crystal';
      default:                 return 'placeholder-square';
    }
  }
}
